import { useMemo } from 'react'
import { useScreenSize } from '@ztechapps/common.hook'
import { useWindowSize } from 'react-use'

export type ResponsiveModalSize = {
  width: string
  height: string
}

/**
 * Hook to calculate responsive modal dimensions based on viewport size and the
 * caller-provided maximized flag.
 *
 * Uses the existing useScreenSize hook from @ztechapps/common.hook for
 * mobile/desktop detection (768px breakpoint), with an additional tablet
 * breakpoint at 1024px for enhanced responsive behavior.
 *
 * Breakpoints (when not maximized):
 * - Mobile (<768px): 100vw x 100vh — full-screen sheet, matching the
 *   Zendesk widget so the chat doesn't leak storefront content on the edges
 * - Tablet (768-1024px): 60vw x 70vh
 * - Desktop (>1024px): 500px x 600px
 *
 * Maximized behavior:
 * - Mobile: unchanged 100vw x 100vh — the chat is already full-screen there
 *   and the maximize affordance is hidden in the JSX, so the flag is a no-op.
 * - Tablet/Desktop: 95vw x 92vh — near-full-viewport with a small margin so
 *   the surrounding chrome (and the FAB) is still visible.
 *
 * @param isMaximized Whether the chat modal is in its maximized state.
 *
 * @example
 * ```tsx
 * const { isMaximized } = useFloatingChatBubbleContext()
 * const { width, height } = useResponsiveModalSize(isMaximized)
 * return <div style={{ width, height }}>...</div>
 * ```
 */
export const useResponsiveModalSize = (isMaximized: boolean): ResponsiveModalSize => {
  const screenSize = useScreenSize({ breakingPoint: 768 })
  const { width: windowWidth } = useWindowSize()

  const size = useMemo(() => {
    // Mobile: <768px (using useScreenSize) — full-screen, no edge padding.
    // Maximize flag is intentionally ignored here: the chat is already
    // full-screen on mobile, and the maximize affordance is hidden from the JSX.
    if (screenSize === 'mobile') {
      return {
        width: '100vw',
        height: '100vh',
      }
    }

    // Maximized on non-mobile: near-full-viewport with a small margin so the
    // FAB and surrounding chrome remain visible.
    if (isMaximized) {
      return {
        width: '95vw',
        height: '92vh',
      }
    }

    // Tablet: 768px - 1024px (desktop by useScreenSize, but narrow window)
    if (screenSize === 'desktop' && windowWidth < 1024) {
      return {
        width: '60vw',
        height: '70vh',
      }
    }

    // Desktop: >1024px or unknown
    return {
      width: '500px',
      height: '600px',
    }
  }, [screenSize, windowWidth, isMaximized])

  return size
}
